import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type {
  ArticleSearchField,
  CandidateArticle,
} from "../../../../../api/types"

/**
 * The one search over every field of a scope article: number, name and
 * supplier code, plus every further master-data field `get_scope` sends
 * (`searchFields`: manufacturer, material group, the customer's own import
 * columns, ...). The article browser's find bar and the add-statement
 * picker match through `articleMatches`; the searches the document suggests
 * stay on number, name and supplier code (`referenceMatches`).
 */

/** One searchable field of a scope article. */
export interface ArticleField {
  /** `articleNumber` | `name` | `supplierCode`, a master-data column
   *  (`material_group`, ...) or `additional_attributes.<header>`. */
  key: string
  /** A known column's translated label. */
  labelKey?: TranslationKeys
  /** Otherwise the customer's own header (or the raw column name), as written. */
  label?: string
  value: string
  lower: string
  /** Separators and case dropped: set for number, name and supplier code
   *  only, where the fold has always applied. A further field is free text
   *  such as a price ("77.19" folds to "7719"): folding it would let an
   *  article-number fragment hit, and the master checkbox then tick, articles
   *  the supplier never declared. */
  folded: string | null
}

/** The resolver's own identity-code normalisation (`normalize_code`,
 *  mc_tools/normalization.py: separators and case dropped), so a number the
 *  document writes "5201 5720", and a suggestion built from it, finds the
 *  pool's "52015720" exactly like M2 did when it bound the statement. */
const foldCode = (s: string) => s.replace(/[\s\-_.]/g, "").toLowerCase()

const ATTRIBUTE_PREFIX = "additional_attributes."

/** Labels of the master-data columns `get_scope` sends, the master-data
 *  screens' own wording. */
const COLUMN_LABELS: Record<string, TranslationKeys> = {
  manufacturer_name: "MC_MD_FIELD_MANUFACTURER",
  manufacturer_id: "MC_MD_MAP_FIELD_ARTICLES_MANUFACTURER_ID",
  country_of_origin: "MC_MD_FIELD_COUNTRY_OF_ORIGIN",
  material_group: "MC_MD_FIELD_MATERIAL_GROUP",
  product_group: "MC_MD_FIELD_PRODUCT_GROUP",
  scip_number: "MC_MD_MAP_FIELD_ARTICLES_SCIP_NUMBER",
}

const codeField = (
  key: string,
  labelKey: TranslationKeys,
  value: string,
): ArticleField => ({
  key,
  labelKey,
  value,
  lower: value.toLowerCase(),
  folded: foldCode(value),
})

const furtherField = ({ field, value }: ArticleSearchField): ArticleField => {
  const labelKey = COLUMN_LABELS[field]
  return {
    key: field,
    ...(labelKey
      ? { labelKey }
      : {
          label: field.startsWith(ATTRIBUTE_PREFIX)
            ? field.slice(ATTRIBUTE_PREFIX.length)
            : field,
        }),
    value,
    lower: value.toLowerCase(),
    folded: null,
  }
}

/** Built once per article object: the pool is a query result whose objects
 *  stay the same across keystrokes, and a 41k-article org would otherwise
 *  rebuild and re-lowercase every field on every one. */
const fieldsByArticle = new WeakMap<CandidateArticle, ArticleField[]>()

/** Every searchable field of `a`, number first, in a stable order. */
export function articleFields(a: CandidateArticle): ArticleField[] {
  let fields = fieldsByArticle.get(a)
  if (!fields) {
    fields = [
      codeField("articleNumber", "MC_DECL_V2_BROWSER_COL_ID", a.articleNumber),
      codeField("name", "MC_DECL_V2_BROWSER_COL_NAME", a.name),
      codeField(
        "supplierCode",
        "MC_DECL_V2_BROWSER_COL_SUPPLIER_CODE",
        a.supplierCode ?? "",
      ),
      ...(a.searchFields ?? []).map(furtherField),
    ]
    fieldsByArticle.set(a, fields)
  }
  return fields
}

/** Whether one field holds a lowercase `needle`. */
export function fieldHas(field: ArticleField, needle: string): boolean {
  if (field.lower.includes(needle)) return true
  if (field.folded === null) return false
  const folded = foldCode(needle)
  return folded.length > 0 && field.folded.includes(folded)
}

/** Whether any lowercase needle hits any field of `a` (OR): the reviewer's
 *  own search. */
export function articleMatches(
  a: CandidateArticle,
  needles: string[],
): boolean {
  const fields = articleFields(a)
  return needles.some((n) => fields.some((f) => fieldHas(f, n)))
}

/** The fields a document's article reference names. */
const REFERENCE_KEYS = new Set(["articleNumber", "name", "supplierCode"])

/** Whether any lowercase needle hits number, name or supplier code (OR): a
 *  search the document suggests. Its terms are the document's own article
 *  references; matched against every field, a listed number "4711" would
 *  also find an EAN "4004711000012", and the master checkbox would then tick
 *  an article the supplier never declared. */
export function referenceMatches(
  a: CandidateArticle,
  needles: string[],
): boolean {
  const fields = articleFields(a).filter((f) => REFERENCE_KEYS.has(f.key))
  return needles.some((n) => fields.some((f) => fieldHas(f, n)))
}
